/**
 * סגנונות צביעה: מאט, זהוב, פסטל, מתכתי, ניאון
 */

function hexToRgb(hex) {
  const n = hex.replace('#', '')
  if (n.length !== 6) return null
  return [
    parseInt(n.slice(0, 2), 16),
    parseInt(n.slice(2, 4), 16),
    parseInt(n.slice(4, 6), 16),
  ]
}

function rgbToHex(r, g, b) {
  const toHex = (x) => Math.round(Math.max(0, Math.min(255, x))).toString(16).padStart(2, '0')
  return `#${toHex(r)}${toHex(g)}${toHex(b)}`
}

function rgbToHsl(r, g, b) {
  r /= 255; g /= 255; b /= 255
  const max = Math.max(r, g, b), min = Math.min(r, g, b)
  let h, s, l = (max + min) / 2
  if (max === min) {
    h = s = 0
  } else {
    const d = max - min
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min)
    switch (max) {
      case r: h = ((g - b) / d + (g < b ? 6 : 0)) / 6; break
      case g: h = ((b - r) / d + 2) / 6; break
      default: h = ((r - g) / d + 4) / 6
    }
  }
  return [h * 360, s * 100, l * 100]
}

function hslToRgb(h, s, l) {
  h /= 360; s /= 100; l /= 100
  let r, g, b
  if (s === 0) {
    r = g = b = l
  } else {
    const q = l < 0.5 ? l * (1 + s) : l + s - l * s
    const p = 2 * l - q
    r = hue2rgb(p, q, h + 1/3)
    g = hue2rgb(p, q, h)
    b = hue2rgb(p, q, h - 1/3)
  }
  return [r * 255, g * 255, b * 255]
}

function hue2rgb(p, q, t) {
  if (t < 0) t += 1
  if (t > 1) t -= 1
  if (t < 1/6) return p + (q - p) * 6 * t
  if (t < 1/2) return q
  if (t < 2/3) return p + (q - p) * (2/3 - t) * 6
  return p
}

/** המרה מצבע HEX ל-HSL (לפלטת ספקטרום) */
export function hexToHsl(hex) {
  const rgb = hexToRgb(hex)
  if (!rgb) return [0, 0, 50]
  return rgbToHsl(rgb[0], rgb[1], rgb[2])
}

/** המרה מ-HSL ל-HEX */
export function hslToHex(h, s, l) {
  const [r, g, b] = hslToRgb(h, s, l)
  return rgbToHex(r, g, b)
}

/** מאט – הפחתת רוויה, מראה שטוח */
export function matte(hex, amount = 0.6) {
  const rgb = hexToRgb(hex)
  if (!rgb) return hex
  const [h, s, l] = rgbToHsl(rgb[0], rgb[1], rgb[2])
  const newS = s * (1 - amount)
  const [r, g, b] = hslToRgb(h, newS, l)
  return rgbToHex(r, g, b)
}

/** פסטל – ערבוב עם לבן */
export function pastel(hex, whiteAmount = 0.5) {
  const rgb = hexToRgb(hex)
  if (!rgb) return hex
  const r = rgb[0] + (255 - rgb[0]) * whiteAmount
  const g = rgb[1] + (255 - rgb[1]) * whiteAmount
  const b = rgb[2] + (255 - rgb[2]) * whiteAmount
  return rgbToHex(r, g, b)
}

/** ניאון – הגברת רוויה */
export function neon(hex, factor = 1.4) {
  const rgb = hexToRgb(hex)
  if (!rgb) return hex
  const [h, s, l] = rgbToHsl(rgb[0], rgb[1], rgb[2])
  const newS = Math.min(100, s * factor)
  const [r, g, b] = hslToRgb(h, newS, l)
  return rgbToHex(r, g, b)
}

/** מתכתי – גוון אפור-כסוף עם נגיעה מהצבע */
export function metallic(hex, silverAmount = 0.7) {
  const rgb = hexToRgb(hex)
  if (!rgb) return hex
  const gray = (rgb[0] * 0.299 + rgb[1] * 0.587 + rgb[2] * 0.114)
  const r = rgb[0] * (1 - silverAmount) + gray * silverAmount
  const g = rgb[1] * (1 - silverAmount) + gray * silverAmount
  const b = rgb[2] * (1 - silverAmount) + gray * silverAmount
  return rgbToHex(r, g, b)
}

/** זהוב – ערבוב עם גוון זהב (הצבע משפיע על הגוון) */
export function goldTint(hex, goldAmount = 0.75) {
  const rgb = hexToRgb(hex)
  if (!rgb) return hex
  const gold = [218, 165, 32]   // dark gold
  const light = [255, 215, 0]   // gold
  const r = rgb[0] * (1 - goldAmount) + (gold[0] + light[0]) / 2 * goldAmount
  const g = rgb[1] * (1 - goldAmount) + (gold[1] + light[1]) / 2 * goldAmount
  const b = rgb[2] * (1 - goldAmount) + (gold[2] + light[2]) / 2 * goldAmount
  return rgbToHex(r, g, b)
}

/** צבע מים – רך, נספג, מתפשט (גוון מעט דהוי) */
export function watercolor(hex, soft = 0.25) {
  const rgb = hexToRgb(hex)
  if (!rgb) return hex
  const [h, s, l] = rgbToHsl(rgb[0], rgb[1], rgb[2])
  const newS = s * (1 - soft)
  const newL = Math.min(100, l + 8)
  const [r, g, b] = hslToRgb(h, newS, newL)
  return rgbToHex(r, g, b)
}

/** צבע שמן – עשיר, מתערבב חזק */
export function oil(hex) {
  const rgb = hexToRgb(hex)
  if (!rgb) return hex
  const [h, s, l] = rgbToHsl(rgb[0], rgb[1], rgb[2])
  const newS = Math.min(100, s * 1.15)
  const newL = Math.max(0, l - 4)
  const [r, g, b] = hslToRgb(h, newS, newL)
  return rgbToHex(r, g, b)
}

/** אקריליק – מתערבב, מראה אחיד */
export function acrylic(hex, amount = 0.12) {
  return matte(hex, amount)
}

/** פחם/גיר שחור – גוון אפור-שחור */
export function charcoal(hex, grayAmount = 0.85) {
  const rgb = hexToRgb(hex)
  if (!rgb) return hex
  const lum = rgb[0] * 0.299 + rgb[1] * 0.587 + rgb[2] * 0.114
  const r = rgb[0] * (1 - grayAmount) + lum * grayAmount
  const g = rgb[1] * (1 - grayAmount) + lum * grayAmount
  const b = rgb[2] * (1 - grayAmount) + lum * grayAmount
  return rgbToHex(r, g, b)
}

/** שפכטל – מריחה עבה, גוון מעט דחוס (אימפסטו) */
export function spachtel(hex) {
  const rgb = hexToRgb(hex)
  if (!rgb) return hex
  const [h, s, l] = rgbToHsl(rgb[0], rgb[1], rgb[2])
  const newS = Math.min(100, s * 1.08)
  const newL = Math.max(2, Math.min(98, l - 2))
  const [r, g, b] = hslToRgb(h, newS, newL)
  return rgbToHex(r, g, b)
}

export const PAINT_STYLES = [
  { id: 'normal', label: 'רגיל', icon: '●' },
  { id: 'matte', label: 'מאט', icon: '◐' },
  { id: 'gold', label: 'זהוב', icon: '✦' },
  { id: 'pastel', label: 'פסטל', icon: '○' },
  { id: 'metallic', label: 'מתכתי', icon: '◇' },
  { id: 'neon', label: 'ניאון', icon: '◉' },
  { id: 'watercolor', label: 'צבע מים', icon: '〰' },
  { id: 'oil', label: 'שמן', icon: '◆' },
  { id: 'acrylic', label: 'אקריליק', icon: '▪' },
  { id: 'charcoal', label: 'פחם', icon: '◼' },
  { id: 'spachtel', label: 'שפכטל', icon: '▬' },
]

/** התנהגות ציור לפי מדיה: נספיגה, ערבוב, מריחה */
export function getPaintBehavior(styleId) {
  switch (styleId) {
    case 'watercolor':
      return {
        brushAlpha: 0.72,
        brushSpread: 1.85,
        compositeOperation: 'multiply',
        smudgeStrength: 0.28,
        smudgeRadiusFactor: 1.4,
        smudgePull: 8,
      }
    case 'oil':
      return {
        brushAlpha: 0.95,
        brushSpread: 1.15,
        compositeOperation: 'multiply',
        smudgeStrength: 0.58,
        smudgeRadiusFactor: 1.5,
        smudgePull: 6,
      }
    case 'acrylic':
      return {
        brushAlpha: 0.9,
        brushSpread: 1.2,
        compositeOperation: 'source-over',
        smudgeStrength: 0.48,
        smudgeRadiusFactor: 1.35,
        smudgePull: 5,
      }
    case 'pastel':
      return {
        brushAlpha: 0.88,
        brushSpread: 1.25,
        compositeOperation: 'source-over',
        smudgeStrength: 0.42,
        smudgeRadiusFactor: 1.5,
        smudgePull: 10,
      }
    case 'charcoal':
      return {
        brushAlpha: 0.92,
        brushSpread: 1.1,
        compositeOperation: 'source-over',
        smudgeStrength: 0.35,
        smudgeRadiusFactor: 1.2,
        smudgePull: 4,
      }
    case 'spachtel':
      return {
        brushAlpha: 0.97,
        brushSpread: 1.28,
        compositeOperation: 'source-over',
        smudgeStrength: 0.2,
        smudgeRadiusFactor: 1.05,
        smudgePull: 2,
        lineCap: 'butt',
        lineJoin: 'miter',
      }
    default:
      return {
        brushAlpha: 1,
        brushSpread: 1,
        compositeOperation: 'source-over',
        smudgeStrength: 0.38,
        smudgeRadiusFactor: 1.1,
        smudgePull: 5,
      }
  }
}

/** מחזיר צבע מעובד לפי הסגנון (למברשת/מילוי/נצנצים) */
export function getStyledColor(hex, styleId) {
  if (!hex) return hex
  switch (styleId) {
    case 'matte': return matte(hex)
    case 'pastel': return pastel(hex)
    case 'neon': return neon(hex)
    case 'metallic': return metallic(hex)
    case 'gold': return goldTint(hex)
    case 'watercolor': return watercolor(hex)
    case 'oil': return oil(hex)
    case 'acrylic': return acrylic(hex)
    case 'charcoal': return charcoal(hex)
    case 'spachtel': return spachtel(hex)
    default: return hex
  }
}

/** צבעים לגרדיאנט זהוב (כהה -> בהיר) */
export const GOLD_GRADIENT = {
  dark: '#b8860b',
  mid: '#daa520',
  light: '#ffd700',
  highlight: '#fff8dc',
}
